/**
 * Local JWT decoding for the HR portal. The browser cannot verify the signature
 * (it never holds the secret) — the server's TenantGuard is the real authority.
 * We only read the claims to scope the UI (role, companyId). Plain CommonJS so
 * node:test can require it and the .vue/.ts screens can import it, mirroring
 * src/axios-auth.js.
 *
 * Replaces the missing §4 /user/info + /auth/codes endpoints: identity and role
 * (and, for companies, companyId/employeeId) come from the access-token claims.
 */

// atob + TextDecoder are globals in modern browsers AND Node 18+ — no Buffer
// dependency, so this runs unchanged in the SPA and under node:test.
function decodeBase64Url(segment) {
  var base64 = String(segment).replace(/-/g, '+').replace(/_/g, '/');
  while (base64.length % 4 !== 0) {
    base64 += '=';
  }
  var binary;
  try {
    binary = typeof atob === 'function'
      ? atob(base64)
      : Buffer.from(base64, 'base64').toString('binary');
  } catch (e) {
    throw new Error('Malformed access token: payload is not valid base64url');
  }
  var bytes = new Uint8Array(binary.length);
  for (var i = 0; i < binary.length; i++) {
    bytes[i] = binary.charCodeAt(i);
  }
  return new TextDecoder().decode(bytes);
}

function decodeJwtPayload(token) {
  var parts = String(token || '').split('.');
  if (parts.length !== 3 || !parts[1]) {
    throw new Error('Malformed access token: expected a three-part JWT');
  }
  var parsed;
  try {
    parsed = JSON.parse(decodeBase64Url(parts[1]));
  } catch (e) {
    throw new Error('Malformed access token: payload is not valid JSON');
  }
  if (!parsed || typeof parsed !== 'object' || !('sub' in parsed)) {
    throw new Error('Malformed access token: missing subject claim');
  }
  return parsed;
}

function idString(value) {
  return value === undefined || value === null ? undefined : String(value);
}

/**
 * Build the non-sensitive session the HR portal scopes on. Every id is a string
 * (matching the backend's BigInt→string serialization). `extra.email` lets the
 * login flow attach the typed email for display.
 */
function sessionFromToken(token, extra) {
  var p = decodeJwtPayload(token);
  var session = {
    sub: idString(p.sub),
    role: p.role,
    companyId: idString(p.companyId),
    insurerId: idString(p.insurerId),
    employeeId: idString(p.employeeId),
  };
  if (extra && extra.email) {
    session.email = extra.email;
  }
  return session;
}

module.exports = {
  decodeJwtPayload: decodeJwtPayload,
  sessionFromToken: sessionFromToken,
};
